/* eslint-disable no-prototype-builtins */
import {
  AppVersion,
  Currency,
  GeneralErrorCause,
  Locale,
  RemoteConstants,
  RemoteConstantsRepositoryType,
  TripBookColor,
  TripBookLanguage,
  TripBookPrice,
  TripBookRemoteConstants,
  TripBookShippingDiscount
} from '51trips-frontend-core'
import { doc, getDoc, updateDoc } from 'firebase/firestore'
import {
  fromEvent,
  map,
  MonoTypeOperatorFunction,
  Observable,
  retry,
  take,
  throwError,
  timer
} from 'rxjs'

import { DEFAULT_LOCALE } from '../constants/AppLanguages'
import { db } from '../firebase'
import appVersionConverter from './converters/AppVersionConverter'
import generalErrorConverter from './converters/GeneralErrorConverter'
import remoteConstantsConverter from './converters/RemoteConstantsConverter'
import tripBookPriceConverter from './converters/TripBookPriceConverter'
import tripBookRemoteConstantsConverter from './converters/TripBookRemoteConstantsConverter'

const OFFLINE_RETRY_COUNT = 3
const OFFLINE_RETRY_DELAY_MS = 2000

/**
 * `getDoc` fails at once while Firestore thinks the client is offline. The
 * constants are mostly loaded in the background, so retry when the browser is
 * back online (or after a short delay on a flaky connection) instead of
 * failing the read.
 */
function retryWhenOffline<T>(): MonoTypeOperatorFunction<T> {
  return retry({
    count: OFFLINE_RETRY_COUNT,
    delay: (error, retryCount) => {
      if (error?.cause !== GeneralErrorCause.unavailable) {
        return throwError(() => error)
      }
      return navigator.onLine
        ? timer(retryCount * OFFLINE_RETRY_DELAY_MS)
        : fromEvent(window, 'online').pipe(take(1))
    }
  })
}

export class RemoteConstantsRepository implements RemoteConstantsRepositoryType {
  fetchRemoteConstants(): Observable<void> {
    return new Observable<void>((subscriber) => {
      getDoc(doc(db, 'constants', 'constants'))
        .then((documentSnapshot) => {
          const remoteConstantsData = documentSnapshot.data()
          if (documentSnapshot.exists() && remoteConstantsData) {
            subscriber.next()
            subscriber.complete()
          } else {
            subscriber.error(
              new Error(
                `Document snapshot at path ${documentSnapshot.ref.path} when fetching constants don't exist`
              )
            )
          }
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(error, 'Fetching constants failed')
          )
        })
    }).pipe(retryWhenOffline())
  }

  fetchRemoteTripBookConstants(): Observable<void> {
    return new Observable<void>((subscriber) => {
      getDoc(doc(db, 'constants', 'tripBook'))
        .then((documentSnapshot) => {
          const remoteTripBookConstantsData = documentSnapshot.data()
          if (documentSnapshot.exists() && remoteTripBookConstantsData) {
            subscriber.next()
            subscriber.complete()
          } else {
            subscriber.error(
              new Error(
                `Document snapshot at path ${documentSnapshot.ref.path} when fetching constants don't exist`
              )
            )
          }
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(error, 'Fetching constants failed')
          )
        })
    }).pipe(retryWhenOffline())
  }

  loadRemoteConstants(locales: Locale[]): Observable<RemoteConstants> {
    return new Observable<RemoteConstants>((subscriber) => {
      getDoc(doc(db, 'constants', 'constants'))
        .then((documentSnapshot) => {
          const remoteConstantsData = documentSnapshot.data()
          if (documentSnapshot.exists() && remoteConstantsData) {
            const remoteConstants = remoteConstantsConverter.fromFirestore(
              documentSnapshot,
              locales
            )
            subscriber.next(remoteConstants)
            subscriber.complete()
          } else {
            subscriber.error(
              new Error(
                `Document snapshot at path ${documentSnapshot.ref.path} when loading remote constants doesn't exist`
              )
            )
          }
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              'Loading remote constants failed'
            )
          )
        })
    }).pipe(retryWhenOffline())
  }

  loadTripBookRemoteConstants(
    locales: Locale[],
    currency: Currency
  ): Observable<TripBookRemoteConstants> {
    return new Observable<TripBookRemoteConstants>((subscriber) => {
      getDoc(doc(db, 'constants', 'tripBook'))
        .then((documentSnapshot) => {
          const remoteTripBookConstantsData = documentSnapshot.data()
          if (documentSnapshot.exists() && remoteTripBookConstantsData) {
            const tripBookRemoteConstants = tripBookRemoteConstantsConverter.fromFirestore(
              documentSnapshot,
              locales,
              currency
            )
            subscriber.next(tripBookRemoteConstants)
            subscriber.complete()
          } else {
            subscriber.error(
              new Error(
                `Document snapshot at path ${documentSnapshot.ref.path} when loading remote constants doesn't exist`
              )
            )
          }
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              'Loading remote constants failed'
            )
          )
        })
    }).pipe(retryWhenOffline())
  }

  loadMinimumAppVersionAndroid(): Observable<AppVersion> {
    return new Observable<AppVersion>((subscriber) => {
      getDoc(doc(db, 'constants', 'constants'))
        .then((documentSnapshot) => {
          const remoteConstantsData = documentSnapshot.data()
          const minimumAppVersionAndroid = appVersionConverter(
            remoteConstantsData?.minimumAppVersionRequiredForAndroid
          )
          if (minimumAppVersionAndroid) {
            subscriber.next(minimumAppVersionAndroid)
            subscriber.complete()
          } else {
            subscriber.error(
              new Error(
                `Document snapshot at path ${documentSnapshot.ref.path} when loading minimum app version android doesn't exist`
              )
            )
          }
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              'Loading minimum app version android failed'
            )
          )
        })
    }).pipe(retryWhenOffline())
  }

  loadMinimumAppVersionIOS(): Observable<AppVersion> {
    return new Observable<AppVersion>((subscriber) => {
      getDoc(doc(db, 'constants', 'constants'))
        .then((documentSnapshot) => {
          const remoteConstantsData = documentSnapshot.data()
          const minimumAppVersionIOS = appVersionConverter(
            remoteConstantsData?.minimumAppVersionRequiredForIOS
          )
          if (minimumAppVersionIOS) {
            subscriber.next(minimumAppVersionIOS)
            subscriber.complete()
          } else {
            subscriber.error(
              new Error(
                `Document snapshot at path ${documentSnapshot.ref.path} when loading minimum app version iOS doesn't exist`
              )
            )
          }
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              'Loading minimum app version ios failed'
            )
          )
        })
    }).pipe(retryWhenOffline())
  }

  loadTripBookURL(): Observable<string> {
    return new Observable<string>((subscriber) => {
      getDoc(doc(db, 'constants', 'constants'))
        .then((documentSnapshot) => {
          const remoteConstantsData = documentSnapshot.data()
          if (
            documentSnapshot.exists() &&
            remoteConstantsData &&
            typeof remoteConstantsData.tripBookURL === 'string'
          ) {
            subscriber.next(remoteConstantsData.tripBookURL)
            subscriber.complete()
          } else {
            subscriber.error(
              new Error(
                `Document snapshot at path ${documentSnapshot.ref.path} when loading personalized trip book URL doesn't exist`
              )
            )
          }
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              'Loading personalized trip book URL failed'
            )
          )
        })
    }).pipe(retryWhenOffline())
  }

  setTripBookPrices(tripBookPrices: TripBookPrice[]): Observable<void> {
    return new Observable((subscriber) => {
      updateDoc(doc(db, 'constants', 'tripBook'), {
        tripBookPrices: tripBookPrices.map((tripBookPrice) =>
          tripBookPriceConverter.toFirestore(tripBookPrice)
        )
      })
        .then(() => {
          subscriber.next()
          subscriber.complete()
        })
        .catch((error) => {
          subscriber.error(
            generalErrorConverter.fromFirebaseFirestoreError(
              error,
              `Updating tripBookPrices ${tripBookPrices} failed`
            )
          )
        })
    })
  }

  loadTripBookPrices(currency: Currency): Observable<TripBookPrice[]> {
    return this.loadTripBookRemoteConstants([DEFAULT_LOCALE], currency).pipe(
      map((constants) => constants.tripBookPrices)
    )
  }
  loadTripBookColors(): Observable<TripBookColor[]> {
    return this.loadTripBookRemoteConstants([DEFAULT_LOCALE], Currency.eur).pipe(
      map((constants) => constants.tripBookColors)
    )
  }
  loadTripBookLanguages(): Observable<TripBookLanguage[]> {
    return this.loadTripBookRemoteConstants([DEFAULT_LOCALE], Currency.eur).pipe(
      map((constants) => constants.tripBookLanguages)
    )
  }
  loadTripBookShippingCountriesIDs(): Observable<string[]> {
    return this.loadTripBookRemoteConstants([DEFAULT_LOCALE], Currency.eur).pipe(
      map((constants) => constants.shippingCountriesNumericCodes)
    )
  }
  loadTripBookShippingDiscounts(): Observable<TripBookShippingDiscount[]> {
    return this.loadTripBookRemoteConstants([DEFAULT_LOCALE], Currency.eur).pipe(
      map((constants) => constants.tripBookShippingDiscounts)
    )
  }
  loadTripBookDefaultShippingDiscount(currency: Currency): Observable<number> {
    return this.loadTripBookRemoteConstants([DEFAULT_LOCALE], Currency.eur).pipe(
      map((constants) => {
        if (constants.tripBookDefaultShippingDiscount.hasOwnProperty(currency)) {
          return constants.tripBookDefaultShippingDiscount[currency]
        } else {
          return 0
        }
      })
    )
  }
}
